import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

interface CustomerJourneyModalProps {
  customerId: string;
  onClose: () => void;
}

const WORKFLOW_STEPS = [
  'Booking',
  'Document Complete',
  'Loan Login',
  'Verification',
  'Account Opening',
  'Agreement',
  'Down Payment',
  'Disbursement',
  'RTO (Passing)',
  'Delivery'
];

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Pending:   { bg: '#FEF3C7', text: '#92400E' },
  Hold:      { bg: '#FEE2E2', text: '#991B1B' },
  Done:      { bg: '#D1FAE5', text: '#065F46' },
  Cancelled: { bg: '#FEE2E2', text: '#DC2626' },
};

const ACTION_ICONS: Record<string, string> = {
  admin_instruct: '📢',
  staff_update: '💬',
  hold_reason: '🔴',
  status_change: '🔄',
  note: '📝',
  customer_created: '🎉'
};

const CustomerJourneyModal: React.FC<CustomerJourneyModalProps> = ({ customerId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [journeyData, setJourneyData] = useState<any>(null);

  useEffect(() => {
    const fetchJourney = async () => {
      setLoading(true);
      try {
        const res = await apiService.getCrmCustomerJourney(customerId);
        if (res.success) setJourneyData(res.data);
      } catch (e) { console.error('Error fetching journey', e); }
      finally { setLoading(false); }
    };
    fetchJourney();
  }, [customerId]);

  if (loading || !journeyData) {
    return (
      <div className="crm-modal-overlay" onClick={onClose}>
        <div className="crm-modal" style={{ width: '800px', height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
          <div className="crm-loading"><div className="crm-spinner"></div> Loading Journey...</div>
        </div>
      </div>
    );
  }

  const { customer, originLead, activities } = journeyData;
  const stepMap: Record<string, any> = {};
  (customer.crm_workflow_steps || []).forEach((s: any) => { stepMap[s.step_name] = s; });

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + 
      ' • ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose} style={{ zIndex: 1000 }}>
      <div 
        className="crm-modal"
        style={{ width: '900px', maxWidth: '95vw', height: '85vh', display: 'flex', flexDirection: 'column', background: '#F8FAFC', padding: 0, overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header - Customer Profile */}
        <div style={{ background: 'white', padding: '24px 32px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: 700, boxShadow: '0 4px 12px rgba(79,70,229,0.3)' }}>
              {customer.customer_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', color: '#1E293B', fontFamily: 'Inter, sans-serif' }}>{customer.customer_name}</h2>
              <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#64748B' }}>
                <span title="Phone">📞 {customer.phone}</span>
                {customer.car_details && <span title="Car Details">🚗 {customer.car_details}</span>}
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                {originLead ? (
                  <span style={{ background: '#EFF6FF', color: '#1D4ED8', padding: '4px 8px', borderRadius: '4px', fontWeight: 600 }}>
                    Converted Lead (By {originLead.assigned_to?.name || 'Sales Staff'})
                  </span>
                ) : (
                  <span style={{ background: '#F1F5F9', color: '#475569', padding: '4px 8px', borderRadius: '4px', fontWeight: 600 }}>
                    Walk-in Customer
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94A3B8' }}>✕</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          
          {/* Left Panel: Workflow Progress */}
          <div style={{ width: '280px', background: 'white', borderRight: '1px solid #E2E8F0', overflowY: 'auto', padding: '24px' }}>
            <h3 style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94A3B8', marginBottom: '20px' }}>Workflow Progress</h3>
            
            <div style={{ position: 'relative' }}>
              {/* Connecting Line */}
              <div style={{ position: 'absolute', left: '15px', top: '15px', bottom: '20px', width: '2px', background: '#E2E8F0', zIndex: 0 }}></div>

              {WORKFLOW_STEPS.map((step, idx) => {
                const s = stepMap[step];
                if (!s) return null;
                const status = s.status || 'Pending';
                const colorMap = STATUS_COLORS[status];
                const isDone = status === 'Done';
                const isHold = status === 'Hold';

                return (
                  <div key={step} style={{ display: 'flex', gap: '16px', marginBottom: '24px', position: 'relative', zIndex: 1 }}>
                    {/* Status Node */}
                    <div style={{ 
                      width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                      background: isDone ? colorMap.bg : 'white',
                      border: `2px solid ${isDone ? colorMap.text : isHold ? '#EF4444' : '#CBD5E1'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: colorMap.text, fontSize: '14px', fontWeight: 700,
                      boxShadow: isHold ? '0 0 0 4px #FEE2E2' : 'none'
                    }}>
                      {isDone ? '✓' : isHold ? '!' : idx + 1}
                    </div>
                    
                    {/* Step Details */}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: isDone ? '#0F172A' : '#64748B', marginBottom: '4px' }}>{step}</div>
                      <span style={{ 
                        display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                        background: colorMap.bg, color: colorMap.text
                      }}>
                        {status}
                      </span>
                      {s.updated_by?.name && status !== 'Pending' && (
                        <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px' }}>by {s.updated_by.name}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel: Activity Feed */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px', background: '#F8FAFC' }}>
            <h3 style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94A3B8', marginBottom: '20px' }}>Complete Activity Timeline</h3>
            
            {activities.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94A3B8', marginTop: '60px' }}>No activities logged yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {activities.map((act: any) => {
                  const isAction = act.action_type === 'status_change' || act.action_type === 'customer_created';
                  const fromAdmin = act.action_type === 'admin_instruct';
                  
                  return (
                    <div key={act.id} style={{ display: 'flex', gap: '16px', opacity: isAction ? 0.8 : 1 }}>
                      {/* Icon */}
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                        background: fromAdmin ? '#E0E7FF' : isAction ? '#F1F5F9' : '#DBEAFE',
                        color: fromAdmin ? '#4338CA' : isAction ? '#64748B' : '#2563EB',
                        border: `1px solid ${fromAdmin ? '#C7D2FE' : isAction ? '#E2E8F0' : '#BFDBFE'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px'
                      }}>
                        {ACTION_ICONS[act.action_type] || '💬'}
                      </div>
                      
                      {/* Content Card */}
                      <div style={{ 
                        flex: 1, background: 'white', padding: '16px', borderRadius: '12px',
                        border: '1px solid #E2E8F0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: fromAdmin ? '#4338CA' : '#1E293B' }}>
                            {act.performed_by?.name || 'System Auto-Event'}
                            {act.performed_by?.role && <span style={{ fontWeight: 400, color: '#64748B' }}> ({act.performed_by.role})</span>}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94A3B8' }}>{formatTime(act.created_at)}</div>
                        </div>
                        <div style={{ fontSize: '14px', color: '#334155', lineHeight: 1.5, fontWeight: fromAdmin ? 500 : 400 }}>
                          {act.description}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default CustomerJourneyModal;
